import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { apiClient } from '@rti/shared';

interface Toast {
  id: number;
  title: string;
  message: string;
}

interface NotificationToastContextType {
  showToast: (title: string, message: string) => void;
  unreadCount: number;
  clearUnreadCount: () => void;
}

const NotificationToastContext = createContext<NotificationToastContextType | undefined>(undefined);

export const useNotificationToast = (): NotificationToastContextType => {
  const ctx = useContext(NotificationToastContext);
  if (!ctx) throw new Error('useNotificationToast must be used within NotificationToastProvider');
  return ctx;
};

const TOKEN_KEY = 'access_token';

const NotificationToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenId = useRef<number>(0);
  const toastIdCounter = useRef<number>(0);

  const showToast = useCallback((title: string, message: string) => {
    const id = ++toastIdCounter.current;
    setToasts((prev) => {
      if (prev.length >= 3) return prev;
      return [...prev, { id, title, message }];
    });
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 10000);
  }, []);

  const clearUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const dismissToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    const poll = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      try {
        const notifications = await apiClient.getInAppNotifications();
        if (notifications.length === 0) return;
        const maxId = Math.max(...notifications.map((n) => n.id));
        if (lastSeenId.current === 0) {
          lastSeenId.current = maxId;
          const initialUnread = notifications.filter((n) => !n.is_read).length;
          setUnreadCount(initialUnread);
          return;
        }
        const newOnes = notifications.filter((n) => n.id > lastSeenId.current).slice(0, 3);
        newOnes.forEach((n) => showToast(n.title, n.message));
        if (newOnes.length > 0) {
          lastSeenId.current = maxId;
          setUnreadCount((prev) => prev + newOnes.length);
        }
      } catch {}
    };

    poll();
    const interval = setInterval(poll, 600000);
    return () => clearInterval(interval);
  }, [showToast]);

  return (
    <NotificationToastContext.Provider value={{ showToast, unreadCount, clearUnreadCount }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="bg-rs-jet border border-rs-green/30 rounded-lg px-4 py-3 shadow-lg w-80"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-rs-off-white">{toast.title}</span>
                <span className="text-xs text-rs-off-white/70">{toast.message}</span>
              </div>
              <button
                onClick={() => dismissToast(toast.id)}
                className="text-rs-off-white/40 hover:text-rs-off-white flex-shrink-0 mt-0.5"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </NotificationToastContext.Provider>
  );
};

export default NotificationToastProvider;
